import type { FunctionReturnType } from "convex/server";
import type { Id } from "../../../convex/_generated/dataModel";
import type { api } from "../../../convex/_generated/api";
import { isInsufficientCreditError } from "../../lib/app-errors";
import type {
  LoggingFlowPayload,
  LoggingFlowReviewItem,
} from "../../lib/logging-flow";

// Photo logging flow pure logic (plan todo 13). The component
// (PhotoLogFlow.tsx) is a thin shell over this reducer + these controllers so
// the repo's node-env test pattern (renderToStaticMarkup + pure functions)
// can assert every step transition, the review-payload mapping, and the
// credit/failure error split without a DOM.

export type MealImageAnalysis = FunctionReturnType<typeof api.analyze.analyzeMealImage>;

export type PhotoFlowSource = "photo" | "upload";

export type PhotoFlowPhase =
  | "pick" // choose capture / library / webcam
  | "uploading"
  | "upload-error"
  | "note" // uploaded; optional note before analysis
  | "analyzing"
  | "analysis-error";

export interface PhotoFlowState {
  readonly phase: PhotoFlowPhase;
  // Retained across upload attempts so Retry needs no second picker round.
  readonly file: File | null;
  readonly fileName: string | null;
  readonly previewUrl: string | null;
  readonly storageId: string | null;
  readonly note: string;
  readonly errorMessage: string | null;
  readonly creditDrawerOpen: boolean;
}

export const INITIAL_PHOTO_FLOW_STATE: PhotoFlowState = {
  phase: "pick",
  file: null,
  fileName: null,
  previewUrl: null,
  storageId: null,
  note: "",
  errorMessage: null,
  creditDrawerOpen: false,
};

export type PhotoFlowAction =
  | { readonly type: "filePicked"; readonly file: File }
  | { readonly type: "fileRejected"; readonly message: string }
  | { readonly type: "uploadSucceeded"; readonly storageId: string; readonly previewUrl: string }
  | { readonly type: "uploadFailed"; readonly message: string }
  | { readonly type: "retryUpload" }
  | { readonly type: "noteChanged"; readonly note: string }
  | { readonly type: "analyzeStarted" }
  | { readonly type: "analyzeFailed"; readonly message: string; readonly credits: boolean }
  | { readonly type: "creditDrawerClosed" }
  | { readonly type: "pickDifferentPhoto" };

export function photoFlowReducer(
  state: PhotoFlowState,
  action: PhotoFlowAction,
): PhotoFlowState {
  switch (action.type) {
    case "filePicked":
      return {
        ...state,
        phase: "uploading",
        file: action.file,
        fileName: action.file.name,
        errorMessage: null,
      };
    case "fileRejected":
      return { ...state, phase: "pick", file: null, errorMessage: action.message };
    case "uploadSucceeded":
      if (state.phase !== "uploading") return state;
      return {
        ...state,
        phase: "note",
        storageId: action.storageId,
        previewUrl: action.previewUrl,
        errorMessage: null,
      };
    case "uploadFailed":
      if (state.phase !== "uploading") return state;
      return { ...state, phase: "upload-error", errorMessage: action.message };
    case "retryUpload":
      if (state.phase !== "upload-error" || state.file === null) return state;
      return { ...state, phase: "uploading", errorMessage: null };
    case "noteChanged":
      return { ...state, note: action.note };
    case "analyzeStarted":
      // From the note step OR directly from a failed analysis (Retry).
      if (
        (state.phase !== "note" && state.phase !== "analysis-error") ||
        state.storageId === null
      ) {
        return state;
      }
      return { ...state, phase: "analyzing", errorMessage: null };
    case "analyzeFailed":
      if (state.phase !== "analyzing") return state;
      if (action.credits) {
        // Credits are recoverable in place: back to the note step with the
        // drawer open; nothing the user typed is lost.
        return { ...state, phase: "note", creditDrawerOpen: true, errorMessage: null };
      }
      return { ...state, phase: "analysis-error", errorMessage: action.message };
    case "creditDrawerClosed":
      return { ...state, creditDrawerOpen: false };
    case "pickDifferentPhoto":
      return {
        ...INITIAL_PHOTO_FLOW_STATE,
        creditDrawerOpen: state.creditDrawerOpen,
      };
  }
}

function nullToUndefined<T>(value: T | null): T | undefined {
  return value === null ? undefined : value;
}

// Maps the analyzeMealImage result onto the review payload (todo 12 contract).
// Nullable analysis fields become absent (undefined) review fields — unknown
// nutrition stays unknown, never zero-filled.
export function buildPhotoReviewPayload(
  analysis: MealImageAnalysis,
  storageId: string,
  source: PhotoFlowSource,
): LoggingFlowPayload {
  const items: LoggingFlowReviewItem[] = analysis.items.map((item) => ({
    name: item.name,
    ...(item.quantity === null ? {} : { quantity: item.quantity }),
    ...(item.unit === null ? {} : { unit: item.unit }),
    ...(item.calories === null ? {} : { calories: item.calories }),
    ...(item.proteinG === null ? {} : { proteinG: item.proteinG }),
    ...(item.carbsG === null ? {} : { carbsG: item.carbsG }),
    ...(item.fatG === null ? {} : { fatG: item.fatG }),
    ...(item.fiberG === null ? {} : { fiberG: item.fiberG }),
    confidence: item.confidence,
    dataSource: item.dataSource,
  }));
  const range = analysis.uncertainty;
  const uncertainty =
    range.hasRange && range.lowKcal !== null && range.highKcal !== null
      ? {
          hasRange: true,
          lowKcal: range.lowKcal,
          highKcal: range.highKcal,
          ...(range.reason === null ? {} : { reason: range.reason }),
        }
      : undefined;
  const mealName = nullToUndefined(analysis.mealName);
  const mealType = nullToUndefined(analysis.mealTypeGuess);
  return {
    items,
    ...(uncertainty === undefined ? {} : { uncertainty }),
    imageStorageId: storageId,
    source,
    ...(mealName === undefined ? {} : { name: mealName }),
    ...(mealType === undefined ? {} : { mealType }),
  };
}

// "Enter manually" fallback after a failed analysis: blank review, keeping the
// uploaded photo attached when one exists.
export function buildManualReviewPayload(
  storageId: string | null,
  source: PhotoFlowSource,
): LoggingFlowPayload {
  return {
    items: [],
    source,
    ...(storageId === null ? {} : { imageStorageId: storageId }),
  };
}

export type AnalyzeMealImageFn = (args: {
  storageId: Id<"_storage">;
  note?: string;
}) => Promise<MealImageAnalysis>;

export type PhotoAnalysisOutcome =
  | { readonly ok: true; readonly analysis: MealImageAnalysis }
  | { readonly ok: false; readonly credits: boolean; readonly message: string };

export type CameraErrorKind = "unsupported" | "denied" | "unknown";

const CAMERA_ERROR_MESSAGES: Record<CameraErrorKind, string> = {
  unsupported:
    "This device or browser doesn't support camera capture. You can upload a photo instead.",
  denied:
    "Camera permission was denied. Allow camera access in your browser settings, or upload a photo instead.",
  unknown: "Couldn't start the camera. Try again or upload a photo instead.",
};

// Maps getUserMedia failures onto human-readable copy. NotFoundError (no
// camera hardware) reads like "unsupported"; NotReadableError and everything
// else fall through to the generic retry message.
export function describeCameraError(
  error: unknown,
  mediaDevicesSupported: boolean,
): { readonly kind: CameraErrorKind; readonly message: string } {
  if (!mediaDevicesSupported) {
    return { kind: "unsupported", message: CAMERA_ERROR_MESSAGES.unsupported };
  }
  const name = error instanceof Error ? error.name : "";
  const kind: CameraErrorKind =
    name === "NotAllowedError" || name === "SecurityError"
      ? "denied"
      : name === "NotFoundError"
        ? "unsupported"
        : "unknown";
  return { kind, message: CAMERA_ERROR_MESSAGES[kind] };
}

// Pure controller around the analyze action (todo-21 pattern): classifies
// failures so the component only dispatches — INSUFFICIENT_CREDITS opens the
// CreditDrawer, everything else offers Retry / Enter manually.
export async function runMealImageAnalysis(
  analyze: AnalyzeMealImageFn,
  args: { storageId: Id<"_storage">; note?: string },
): Promise<PhotoAnalysisOutcome> {
  try {
    const analysis = await analyze(args);
    console.log("[Frontend][PhotoLogFlow] Meal image analysis completed", {
      itemCount: analysis.items.length,
      hasUncertaintyRange: analysis.uncertainty.hasRange,
    });
    return { ok: true, analysis };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    const credits = isInsufficientCreditError(error);
    console.error("[Frontend][PhotoLogFlow] Meal image analysis failed", {
      credits,
      error: message,
    });
    return { ok: false, credits, message };
  }
}
